import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/TranslationContext";
import { CalendarClock, Send, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PACKS = [
  { key: "board", label: "Board pack" },
  { key: "assurance", label: "Assurance pack" },
];

export const ScheduledPackCard = () => {
  const { tx } = useI18n();
  const [schedule, setSchedule] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [packType, setPackType] = useState("board");
  const [recipients, setRecipients] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = () => {
    api.get("/governance/schedule").then(({ data }) => {
      setSchedule(data);
      setEnabled(data.enabled);
      setPackType(data.pack_type || "board");
      setRecipients((data.recipients || []).join(", "));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const parseRecipients = () =>
    recipients.split(/[,\n;]/).map((r) => r.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/governance/schedule", {
        enabled, pack_type: packType, recipients: parseRecipients(),
      });
      setSchedule(data);
      toast.success(enabled ? "Scheduled delivery enabled." : "Schedule saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const body = { pack_type: packType };
      if (testTo.trim()) body.recipient = testTo.trim();
      const { data } = await api.post("/governance/schedule/send-test", body);
      toast.success(`Sent to ${data.sent} recipient(s)${data.failed ? `, ${data.failed} failed` : ""}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test send failed.");
    } finally {
      setTesting(false);
    }
  };

  if (!schedule) return null;

  return (
    <section className="mt-12" data-testid="scheduled-pack-card">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-ek-ink">
        <CalendarClock size={18} className="text-ek-purple" /> {tx("Scheduled delivery")}
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        {tx("Automatically email the board or assurance pack to your board and auditors at every quarter-end.")}
      </p>

      <div className="mt-4 rounded-md border border-black/5 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex items-center gap-2.5">
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              data-testid="schedule-enabled-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-ek-purple" : "bg-ek-ink/20"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <span className="text-sm font-semibold text-ek-ink">
              {enabled ? tx("Quarterly delivery on") : tx("Quarterly delivery off")}
            </span>
          </label>

          <div className="flex items-center gap-1 rounded-md border border-black/10 p-0.5">
            {PACKS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPackType(p.key)}
                data-testid={`schedule-pack-${p.key}`}
                className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
                  packType === p.key ? "bg-ek-ink text-white" : "text-ek-ink/65 hover:text-ek-ink"
                }`}
              >
                {tx(p.label)}
              </button>
            ))}
          </div>
        </div>

        <label className="mt-5 block text-xs font-semibold text-ek-ink/60">
          {tx("Recipients")} <span className="font-normal text-ek-ink/65">({tx("comma-separated")})</span>
        </label>
        <textarea
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          data-testid="schedule-recipients"
          rows={2}
          placeholder="board@company.com, auditor@firm.com"
          className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
        />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-4">
          <div className="text-xs text-ek-ink/65">
            <span data-testid="schedule-next-run">{tx("Next run")}: <strong>{schedule.next_run}</strong></span>
            {schedule.last_sent_at && (
              <span className="ml-3" data-testid="schedule-last-sent">
                {tx("Last sent")}: {new Date(schedule.last_sent_at).toLocaleString()}
                {schedule.last_result ? ` · ${schedule.last_result.sent} sent` : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={tx("test to… (optional)")}
              data-testid="schedule-test-recipient"
              className="rounded-sm border border-black/15 px-3 py-1.5 text-sm focus:border-ek-purple focus:outline-none"
            />
            <button
              onClick={sendTest}
              disabled={testing}
              data-testid="schedule-send-test"
              className="flex items-center gap-1.5 rounded-sm border border-ek-purple px-3 py-1.5 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5 disabled:opacity-50"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {tx("Send test now")}
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="schedule-save"
              className="ek-btn flex items-center gap-1.5 px-4 py-1.5 text-sm disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {tx("Save")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
